import { Fragment, useEffect, useRef } from 'react';
import { Dialog as HeadlessDialog, Transition } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import Lucide from '../../icons/Lucide';

/**
 * @author tieres-claude-agent
 * @version 1.0.0
 * @since 2026-04-27
 * @description Componente de Modal generico do design system. Abstrai o
 *   HeadlessUI Dialog ja existente em `Dialog/index.tsx` por uma API mais
 *   simples e amigavel — usa `isOpen`, `onClose`, `title`, `size`, com
 *   slots opcionais `<Modal.Body>` e `<Modal.Footer>`.
 *
 *   Vantagens sobre a versao Dialog mais baixa:
 *     - Header com titulo + botao fechar (X) ja inclusos
 *     - Tamanhos pre-definidos (sm/md/lg/xl/full) com padding adequado
 *     - Foco automatico no primeiro elemento focavel
 *     - Click no overlay + tecla Escape fecham (configuravel)
 *     - Body scrollavel automaticamente
 *
 *   Quando NAO usar:
 *     - Caso precise de overlays especificos (drawer, slideover): use o
 *       componente proprio.
 *     - Caso precise de modal de upload com preview de arquivo: ver
 *       `DocumentModal` que ja existe.
 */

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const SIZE_CLASS: Record<ModalSize, string> = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
    full: 'max-w-[95vw] h-[90vh]',
};

export interface ModalProps {
    /** Controla a visibilidade. */
    isOpen: boolean;
    /** Callback quando o usuario fecha (overlay, X, Escape). */
    onClose: () => void;
    /** Titulo exibido no header. Quando ausente, header nao renderiza. */
    title?: string;
    /** Tamanho maximo. Default 'md'. */
    size?: ModalSize;
    /** Quando true, click no overlay nao fecha. Default false. */
    staticBackdrop?: boolean;
    /** Quando true, ESC nao fecha. Default false. */
    disableEscape?: boolean;
    /** Classe extra pro container. */
    className?: string;
    /** Conteudo. Pode usar `<Modal.Body>` e `<Modal.Footer>` ou children diretos. */
    children: React.ReactNode;
    /** Esconde o botao X do header. Util pra modais de confirmacao bloqueante. */
    hideCloseButton?: boolean;
    /** testid pro elemento raiz */
    'data-testid'?: string;
}

function Modal({
    isOpen,
    onClose,
    title,
    size = 'md',
    staticBackdrop = false,
    disableEscape = false,
    className,
    children,
    hideCloseButton = false,
    'data-testid': testId,
}: ModalProps) {
    const initialFocusRef = useRef<HTMLDivElement | null>(null);

    // Bloqueia ESC se disableEscape (HeadlessDialog ja respeita onClose,
    // mas adicionamos handler defensivo pra bloquear no nivel do document).
    useEffect(() => {
        if (!isOpen || !disableEscape) return;
        const handler = (ev: KeyboardEvent) => {
            if (ev.key === 'Escape') ev.stopPropagation();
        };
        document.addEventListener('keydown', handler, true);
        return () => document.removeEventListener('keydown', handler, true);
    }, [isOpen, disableEscape]);

    return (
        <Transition appear as={Fragment} show={isOpen}>
            <HeadlessDialog
                as="div"
                onClose={(value: boolean) => {
                    if (staticBackdrop) return;
                    if (!value) onClose();
                }}
                initialFocus={initialFocusRef}
                className="relative z-[60]"
                data-testid={testId}
            >
                {/* Overlay */}
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/60" aria-hidden="true" />
                </Transition.Child>

                {/* Container */}
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <HeadlessDialog.Panel
                                className={twMerge(
                                    'w-full overflow-hidden rounded-2xl bg-white shadow-2xl',
                                    'flex flex-col',
                                    SIZE_CLASS[size],
                                    className,
                                )}
                            >
                                <div ref={initialFocusRef} tabIndex={-1} />
                                {/* Header */}
                                {(title || !hideCloseButton) && (
                                    <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
                                        {title ? (
                                            <HeadlessDialog.Title
                                                as="h3"
                                                className="text-base font-semibold text-slate-900"
                                            >
                                                {title}
                                            </HeadlessDialog.Title>
                                        ) : (
                                            <span />
                                        )}
                                        {!hideCloseButton && (
                                            <button
                                                type="button"
                                                onClick={onClose}
                                                className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary"
                                                aria-label="Fechar"
                                                data-testid="modal-close-button"
                                            >
                                                <Lucide icon="X" className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                )}

                                {children}
                            </HeadlessDialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </HeadlessDialog>
        </Transition>
    );
}

/** Body scrollavel — padding padrao + flex-1 pra ocupar o espaco do panel. */
function ModalBody({ children, className }: { children: React.ReactNode; className?: string }) {
    return (
        <div className={twMerge('flex-1 overflow-y-auto px-5 py-4', className)}>
            {children}
        </div>
    );
}

/** Footer alinhado a direita — botoes de acao. */
function ModalFooter({ children, className }: { children: React.ReactNode; className?: string }) {
    return (
        <div className={twMerge('flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3', className)}>
            {children}
        </div>
    );
}

Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
export { Modal };
